import {
    deleteObject,
    getDownloadURL,
    getStorage,
    listAll,
    ref,
    StorageReference,
    updateMetadata,
    uploadBytes,
    uploadBytesResumable
} from 'firebase/storage';
import { getFirebase } from './FirebaseAdapater';
import { assert } from 'console';

const PLACEHOLDER_FILE = '.placeholder';

const STORAGE_PATH_REPORTS =
    'gs://' + getFirebase().getConfig().storageBucket + '/pdfs';
const STORAGE_PATH_RAG =
    'gs://' + getFirebase().getConfig().storageBucket + '/rag';

export const STORAGE_KNOWLEDGE_PATH =
    'gs://' + getFirebase().getConfig().storageBucket + '/knowledge';
export type StorageTarget =
    | { type: 'rag'; reportId: string }
    | { type: 'cim' }
    | { type: 'knowledge_base'; folder: string };

export interface StorageFolder {
    path: string;
    name: string;
    files: StorageEntry[];
    folders: StorageFolder[];
}

export interface StorageEntry {
    fileName: string;
    path: string;
}

class StorageAdapater {
    async check_if_report_exists(fileName: string) {
        const storage = getStorage();
        const docRef = ref(storage, `${STORAGE_PATH_REPORTS}/${fileName}`);

        try {
            await getDownloadURL(docRef);
            return true;
        } catch (error) {
            return false;
        }
    }

    async getListFolderContent(path: string): Promise<StorageFolder> {
        const storage = getStorage();
        const rootDir = ref(storage, path);

        const getFolder = async (
            dirRef: StorageReference
        ): Promise<StorageFolder> => {
            try {
                const listResult = await listAll(dirRef);
                const files: StorageEntry[] = listResult.items.map(item => ({
                    fileName: item.name,
                    path: item.fullPath
                }));

                const filesNoPlaceholder = files.filter(
                    file => file.fileName !== PLACEHOLDER_FILE
                );

                const folders: StorageFolder[] = await Promise.all(
                    listResult.prefixes.map(async folderRef => {
                        return await getFolder(folderRef); // folderRef is already a StorageReference
                    })
                );

                return {
                    path: dirRef.fullPath, // use the actual folder being processed
                    name: dirRef.name,
                    files: filesNoPlaceholder,
                    folders
                };
            } catch (error) {
                console.error('Error listing folder contents:', error);
                return {
                    path: dirRef.fullPath,
                    name: dirRef.name,
                    files: [],
                    folders: []
                };
            }
        };

        return await getFolder(rootDir);
    }

    buildStorageFolderPath(storageTarget: StorageTarget, fileName: string) {
        switch (storageTarget.type) {
            case 'cim':
                return `${STORAGE_PATH_REPORTS}/${fileName}`;
            case 'rag':
                return `${STORAGE_PATH_RAG}/${storageTarget.reportId}/${fileName}`;
            case 'knowledge_base':
                if (!storageTarget.folder) {
                    return `${STORAGE_KNOWLEDGE_PATH}/${fileName}`;
                }
                return `${STORAGE_KNOWLEDGE_PATH}/${storageTarget.folder}/${fileName}`;
            default:
                throw new Error('Invalid storage target type');
        }
    }
    async uploadFileToStorage(
        storageTarget: StorageTarget,
        file: File,
        onProgress?: (progress: number) => void
    ) {
        const fb = getFirebase();
        const userId = fb.userId;
        const userEmail = fb.user.email;

        const storage = getStorage();
        //const filePath = `${STORAGE_PATH_RAG}/${dealId}/${file.name}`;
        const filePath = this.buildStorageFolderPath(storageTarget, file.name);
        const storageRef = ref(storage, filePath);
        const upload_task = uploadBytesResumable(storageRef, file);

        upload_task.on(
            'state_changed',
            snapshot => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                if (onProgress) {
                    onProgress(progress);
                }
            },
            error => {
                return null;
            },
            () => {}
        );

        const metadata = {
            customMetadata: {
                userId,
                userEmail
            }
        };
        var snapshot = await upload_task;
        await updateMetadata(storageRef, metadata);

        fb.triggerScanRagFiles();
        return filePath;
    }

    async deleteFileFromStorage(
        storageTarget: StorageTarget,
        fileName: string
    ) {
        const storage = getStorage();
        const filePath = this.buildStorageFolderPath(storageTarget, fileName);
        const storageRef = ref(storage, filePath);
        try {
            await deleteObject(storageRef);
        } catch (error) {
            return false;
        }
        return true;
    }

    async createFolder(storageTarget: StorageTarget, newFolder: string) {
        const path = this.buildStorageFolderPath(storageTarget, newFolder);
        const storage = getStorage();
        const folderRef = ref(storage, path + '/' + PLACEHOLDER_FILE);
        const emptyFile = new Blob([]); // Empty file
        try {
            await uploadBytes(folderRef, emptyFile);
            return true;
        } catch (error) {
            console.error('Error creating folder:', error);
            return false;
        }
    }

    async deleteKnowledgeFile(storagePath: string) {
        assert(
            storagePath.startsWith('knowledge/'),
            'Invalid path for knowledge file deletion'
        );
        const storage = getStorage();
        const fileRef = ref(storage, storagePath);
        try {
            await deleteObject(fileRef);
            return true;
        } catch (error) {
            console.error('Error deleting file:', error);
            return false;
        }
    }
    async deleteFolder(storageTarget: StorageTarget) {
        const storage = getStorage();
        const path = this.buildStorageFolderPath(storageTarget, '');
        const folderRef = ref(storage, path);

        const deleteFolderRec = async (dirRef: StorageReference) => {
            try {
                const listResult = await listAll(dirRef);

                await Promise.all(
                    listResult.prefixes.map(async folderRef => {
                        return await deleteFolderRec(folderRef); // folderRef is already a StorageReference
                    })
                );

                for (var item of listResult.items) {
                    item = ref(storage, item.fullPath);
                    await deleteObject(item).catch(error => {
                        console.error('Error deleting file:', error);
                    });
                }
            } catch (error) {
                console.error(
                    `Error deleting folder: ${dirRef.fullPath} ${error}`
                );
            }
        };

        try {
            await deleteFolderRec(folderRef);
            return true;
        } catch (error) {
            console.error('Error deleting folder:', error);
            return false;
        }
    }

    async uploadReportPdf(file: File, onProgress?: (progress: number) => void) {
        const fb = getFirebase();
        const userId = fb.userId;
        const userEmail = fb.user.email;

        const storage = getStorage();
        const storageRef = ref(storage, `${STORAGE_PATH_REPORTS}/${file.name}`);
        const upload_task = uploadBytesResumable(storageRef, file);

        upload_task.on(
            'state_changed',
            snapshot => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                if (onProgress) {
                    onProgress(progress);
                }
            },
            error => {
                return false;
            },
            () => {}
        );

        const metadata = {
            customMetadata: {
                userId,
                userEmail
            }
        };
        var snapshot = await upload_task;
        await updateMetadata(storageRef, metadata);
        return true;
    }
}

const mStorageService = new StorageAdapater();
export function getStorageAdapater() {
    return mStorageService;
}
